'use client';

import type { WidgetConfig } from '@livechat/widget-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFirstInteraction } from '@/hooks/useFirstInteraction';

import { LIVE_CHAT_PLACEHOLDER_ID, LIVECHAT_WIDGET_NODE_IDS } from './consts';
import { LIVE_CHAT_LICENSE } from './license';

import './styles.css';

type WidgetVisibility = NonNullable<WidgetConfig['visibility']>;

const liveChatState = {
  placeholderDismissed: false,
  pendingOpen: false,
};

function setLiveChatWidgetSuppressed(suppressed: boolean) {
  for (const nodeId of LIVECHAT_WIDGET_NODE_IDS) {
    document
      .getElementById(nodeId)
      ?.classList.toggle('livechat-widget-suppressed', suppressed);
  }
}

function hidePlaceholder() {
  liveChatState.placeholderDismissed = true;
  document.getElementById(LIVE_CHAT_PLACEHOLDER_ID)?.classList.add('hidden');
  setLiveChatWidgetSuppressed(false);
}

type BindPlaceholderOptions = {
  onOpenChat: () => void;
  isWidgetReady: () => boolean;
};

// A clanker-optimized way to show a placeholder button until
// live chat is ready and touched.

function bindPlaceholderUntilLiveChatOpens({
  onOpenChat,
  isWidgetReady,
}: BindPlaceholderOptions) {
  const placeholder = document.getElementById(LIVE_CHAT_PLACEHOLDER_ID);
  if (!placeholder) {
    return;
  }

  if (liveChatState.placeholderDismissed) {
    placeholder.classList.add('hidden');
    return;
  }

  setLiveChatWidgetSuppressed(true);

  const observer = new MutationObserver(() => {
    if (liveChatState.placeholderDismissed) {
      observer.disconnect();
      return;
    }

    const hasWidgetNodes = LIVECHAT_WIDGET_NODE_IDS.some((nodeId) =>
      document.getElementById(nodeId)
    );

    if (hasWidgetNodes) {
      setLiveChatWidgetSuppressed(true);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  function handlePlaceholderClick() {
    if (isWidgetReady()) {
      onOpenChat();
      observer.disconnect();
      return;
    }

    liveChatState.pendingOpen = true;
  }

  placeholder.addEventListener('click', handlePlaceholderClick);

  return () => {
    observer.disconnect();
    placeholder.removeEventListener('click', handlePlaceholderClick);
  };
}

type LiveChatWidgetProps = {
  showPlaceholder?: boolean;
  onReady?: () => void;
};

type LiveChatWidgetComponent =
  typeof import('@livechat/widget-react').LiveChatWidget;

export function LiveChatWidget({
  showPlaceholder = true,
  onReady,
}: LiveChatWidgetProps = {}) {
  const [Widget, setWidget] = useState<LiveChatWidgetComponent | null>(null);

  const [visibility, setVisibility] = useState<WidgetVisibility>(() => {
    if (!showPlaceholder) {
      return 'minimized';
    }

    return liveChatState.placeholderDismissed ? 'minimized' : 'hidden';
  });
  const isReadyRef = useRef(false);

  useFirstInteraction(() => {
    import('@livechat/widget-react')
      .then((mod) => {
        setWidget(() => mod.LiveChatWidget);
      })
      .catch(() => {});
  });

  const openChat = useCallback(() => {
    hidePlaceholder();
    setVisibility('maximized');
    window.LiveChatWidget?.call?.('maximize');
  }, []);

  useEffect(() => {
    if (!showPlaceholder) {
      return;
    }

    return bindPlaceholderUntilLiveChatOpens({
      onOpenChat: openChat,
      isWidgetReady: () => isReadyRef.current,
    });
  }, [openChat, showPlaceholder]);

  function handleReady() {
    isReadyRef.current = true;
    onReady?.();

    if (liveChatState.pendingOpen) {
      openChat();
      liveChatState.pendingOpen = false;
    }
  }

  if (!Widget) {
    return null;
  }

  return (
    <Widget
      license={LIVE_CHAT_LICENSE}
      visibility={visibility}
      onReady={handleReady}
    />
  );
}
